import React, {
  ComponentType,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { General, NotesStack, Shapes, Time } from 'icons/untitledui';
import { usePosthogCapture } from 'events/posthogCapture/_index';
import * as Styled from 'events/styles/EventCard.style';
import { EventStatus } from 'events/_types';
import colours from 'theme/atoms/colours';

type MeasurementPhase = 'measureAll' | 'measureWithChip' | 'done';

const TAG_ITEM_ATTR = 'data-tag-item';

export type { EventStatus };

const STATUS_ICONS: Record<EventStatus, ComponentType<{ size?: number }>> = {
  open: Shapes.Circle,
  resolved: General.CheckCircle,
  auto_resolved: Time.ClockCheck,
  invisible: General.EyeOff,
};

export interface EventCardTagColors {
  bg: string;
  border: string;
  text: string;
}

export interface EventCardTag {
  id: string;
  label: string;
  icon: ComponentType<{ size?: number }>;
  colors?: EventCardTagColors;
}

export interface EventCardProps {
  eventId: string;
  status: EventStatus;
  authors: string[];
  description: string;
  noteCount: number;
  time: string;
  tags: EventCardTag[];
  selected?: boolean;
  isPrivate?: boolean;
  hasUnseenUpdate?: boolean;
  processing?: boolean;
  onClick?: () => void;
}

export default function EventCard({
  eventId,
  status,
  authors,
  description,
  noteCount,
  time,
  tags,
  selected,
  isPrivate,
  hasUnseenUpdate,
  processing,
  onClick,
}: EventCardProps): JSX.Element {
  const { t } = useTranslation('pageEvents');
  const shiftNotesCapture = usePosthogCapture();
  const StatusIconComponent = isPrivate ? General.EyeOff : STATUS_ICONS[status];
  const statusLabel = isPrivate
    ? t('eventCard.privateLabel')
    : t(`eventCard.status.${status}`);
  const processingLabel = t('eventCard.status.processing');
  const notesLabel = t('eventCard.notesCount', { count: noteCount });
  const unseenUpdateLabel = t('eventCard.unseenUpdate');
  const showNoteCount = !processing && status !== 'invisible' && !isPrivate;

  const prevNoteCountRef = useRef(noteCount);
  const [displayedNoteCount, setDisplayedNoteCount] = useState(noteCount);
  const [notePulseKey, setNotePulseKey] = useState(0);

  useEffect(() => {
    const prev = prevNoteCountRef.current;
    prevNoteCountRef.current = noteCount;
    if (noteCount > prev) {
      setNotePulseKey((k) => k + 1);
      const timer = setTimeout(
        () => setDisplayedNoteCount(noteCount),
        Styled.NOTE_COUNT_PULSE_MIDPOINT_MS
      );
      return () => clearTimeout(timer);
    }
    setDisplayedNoteCount(noteCount);
    return undefined;
  }, [noteCount]);

  const tagListRef = useRef<HTMLDivElement>(null);
  const chipRef = useRef<HTMLDivElement>(null);
  const [visibleTagCount, setVisibleTagCount] = useState(tags.length);
  const [phase, setPhase] = useState<MeasurementPhase>('measureAll');
  const overflowCount = tags.length - visibleTagCount;
  const moreLabel = t('eventCard.moreTagsAriaLabel', { count: overflowCount });

  useLayoutEffect(() => {
    setVisibleTagCount(tags.length);
    setPhase('measureAll');
  }, [tags]);

  useLayoutEffect(() => {
    if (phase === 'done') return;
    const container = tagListRef.current;
    if (!container) {
      setPhase('done');
      return;
    }

    const tagEls = Array.from(
      container.querySelectorAll<HTMLElement>(`[${TAG_ITEM_ATTR}]`)
    );
    if (tagEls.length === 0) {
      setPhase('done');
      return;
    }

    if (phase === 'measureAll') {
      const firstRowTop = tagEls[0].offsetTop;
      const secondRowTag = tagEls.find((el) => el.offsetTop > firstRowTop);
      if (!secondRowTag) {
        setPhase('done');
        return;
      }
      const secondRowTop = secondRowTag.offsetTop;
      const firstHiddenIndex = tagEls.findIndex(
        (el) => el.offsetTop > secondRowTop
      );
      if (firstHiddenIndex === -1) {
        setPhase('done');
        return;
      }
      setVisibleTagCount(firstHiddenIndex);
      setPhase('measureWithChip');
      return;
    }

    const chipEl = chipRef.current;
    if (!chipEl) {
      setPhase('done');
      return;
    }
    const tops = [...tagEls.map((el) => el.offsetTop), chipEl.offsetTop];
    const uniqueRows = Array.from(new Set(tops)).sort((a, b) => a - b);
    if (uniqueRows.length <= 2) {
      setPhase('done');
      return;
    }
    if (visibleTagCount <= 1) {
      setPhase('done');
      return;
    }
    setVisibleTagCount(visibleTagCount - 1);
  }, [phase, visibleTagCount]);

  useEffect(() => {
    const container = tagListRef.current;
    if (!container || typeof ResizeObserver === 'undefined') return undefined;
    let lastWidth = container.clientWidth;
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width ?? container.clientWidth;
      if (width === lastWidth) return;
      lastWidth = width;
      setVisibleTagCount(tags.length);
      setPhase('measureAll');
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [tags]);

  const handleClick = () => {
    if (processing) return;
    shiftNotesCapture.eventOpened({ event_id: eventId, status });
    onClick?.();
  };

  return (
    <Styled.Card
      $status={status}
      $selected={selected}
      $isPrivate={isPrivate}
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      <Styled.Header>
        <Styled.StatusIcon
          $status={status}
          $isPrivate={isPrivate}
          aria-label={statusLabel}
        >
          <StatusIconComponent size={16} aria-hidden="true" />
        </Styled.StatusIcon>
        <Styled.Authors>{authors.join(', ')}</Styled.Authors>
        <Styled.HeaderMeta>
          {showNoteCount && (
            <>
              <Styled.NoteCount
                key={notePulseKey || undefined}
                $isPulsing={notePulseKey > 0}
                aria-label={notesLabel}
              >
                <Styled.NoteCountIcon aria-hidden="true">
                  <NotesStack
                    size={18}
                    fillColor={
                      selected ? colours.neutral[200] : colours.shade[0]
                    }
                  />
                </Styled.NoteCountIcon>
                <Styled.MetaText>{displayedNoteCount}</Styled.MetaText>
              </Styled.NoteCount>
              <Styled.MetaDot aria-hidden="true" />
            </>
          )}
          <Styled.MetaText>{time}</Styled.MetaText>
          {hasUnseenUpdate && (
            <Styled.UnseenUpdateDot role="img" aria-label={unseenUpdateLabel} />
          )}
        </Styled.HeaderMeta>
      </Styled.Header>
      <Styled.Description>{description}</Styled.Description>
      {processing ? (
        <Styled.ProcessingPill role="status" aria-live="polite">
          <Styled.ProcessingPillIcon aria-hidden="true">
            <General.Loading02 />
          </Styled.ProcessingPillIcon>
          <Styled.ProcessingPillLabel>
            {processingLabel}
          </Styled.ProcessingPillLabel>
        </Styled.ProcessingPill>
      ) : (
        <Styled.TagList ref={tagListRef}>
          {tags
            .slice(0, visibleTagCount)
            .map(({ id, label, icon: Icon, colors }) => (
              <Styled.Tag
                key={id}
                $colors={colors}
                $selected={selected}
                {...{ [TAG_ITEM_ATTR]: true }}
              >
                <Styled.TagIcon aria-hidden="true">
                  <Icon size={16} />
                </Styled.TagIcon>
                <Styled.TagLabel>{label}</Styled.TagLabel>
              </Styled.Tag>
            ))}
          {overflowCount > 0 && (
            <Styled.MoreChip
              ref={chipRef}
              $selected={selected}
              aria-label={moreLabel}
            >
              <Styled.TagLabel>+{overflowCount}</Styled.TagLabel>
            </Styled.MoreChip>
          )}
        </Styled.TagList>
      )}
    </Styled.Card>
  );
}
